import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  useTheme,
  alpha,
  Stack,
  Avatar,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  Calculate as CalculateIcon,
  Code as CodeIcon,
  Hub as HubIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import CgnatPesquisarClienteDialog from '../components/CgnatPesquisarClienteDialog';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

interface CgnatGrupo {
  id: number;
  nome: string;
  bloco_ip_publico: string;
  range_ip_privado: string;
  chain_inicial?: number;
  clientes_por_ip?: number;
  created_at: string;
  updated_at: string;
}

interface CgnatDado {
  id: number;
  porta_inicial: number;
  porta_final: number;
  ip_privado: string;
  ip_publico: string;
}

interface RangeIpInfo {
  ipInicial: string;
  ipFinal: string;
  qtdIps: number;
}

const calcularInfoRangeIp = (rangeIp: string): RangeIpInfo | null => {
  if (!rangeIp) return null;

  const match = rangeIp.match(/\/(\d+)$/);
  if (!match) return null;

  const cidr = parseInt(match[1], 10);
  const ipBase = rangeIp.split('/')[0].trim();
  if (!ipBase) return null;

  const partesIP = ipBase.split('.').map(Number);
  if (partesIP.length !== 4 || partesIP.some(isNaN)) return null;

  const qtdIps = Math.pow(2, 32 - cidr);
  const mascaraBits = 32 - cidr;
  let ipInicialNum = (partesIP[0] << 24) + (partesIP[1] << 16) + (partesIP[2] << 8) + partesIP[3];
  ipInicialNum = (ipInicialNum >> mascaraBits) << mascaraBits;
  const ipFinalNum = ipInicialNum + qtdIps - 1;

  const numeroParaIp = (num: number) => [
    (num >>> 24) & 0xFF,
    (num >>> 16) & 0xFF,
    (num >>> 8) & 0xFF,
    num & 0xFF,
  ].join('.');

  return {
    ipInicial: numeroParaIp(ipInicialNum),
    ipFinal: numeroParaIp(ipFinalNum),
    qtdIps,
  };
};

const CgnatPage: React.FC = () => {
  const theme = useTheme();
  const [grupos, setGrupos] = useState<CgnatGrupo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');

  // Modais
  const [geradorDialog, setGeradorDialog] = useState<{
    open: boolean;
    grupo: CgnatGrupo | null;
  }>({
    open: false,
    grupo: null,
  });

  const [detalhesDialog, setDetalhesDialog] = useState<{
    open: boolean;
    grupo: CgnatGrupo | null;
  }>({
    open: false,
    grupo: null
  });

  const [scriptDialog, setScriptDialog] = useState<{
    open: boolean;
    grupo: CgnatGrupo | null;
  }>({
    open: false,
    grupo: null
  });
  const [pesquisaDialogOpen, setPesquisaDialogOpen] = useState(false);

  useEffect(() => {
    loadGrupos();
  }, [page, rowsPerPage, searchTerm]);

  const loadGrupos = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response: any = await api.get('/cgnat', { params });

      if (response && response.success) {
        setGrupos(response.data.grupos);
        setTotalCount(response.data.pagination.total);
      }

    } catch (err: any) {
      console.error('Erro ao carregar grupos CGNAT:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este grupo CGNAT?')) {
      return;
    }

    try {
      await api.delete(`/cgnat/${id}`);
      loadGrupos();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao excluir grupo');
    }
  };

  const handleViewDetalhes = (grupo: CgnatGrupo) => {
    setDetalhesDialog({
      open: true,
      grupo
    });
  };

  const handleCloseDetalhes = () => {
    setDetalhesDialog({
      open: false,
      grupo: null
    });
  };

  const handleViewScript = (grupo: CgnatGrupo) => {
    setScriptDialog({
      open: true,
      grupo
    });
  };

  const handleCloseScript = () => {
    setScriptDialog({
      open: false,
      grupo: null
    });
  };

  const handleOpenGerador = () => {
    setGeradorDialog({
      open: true,
      grupo: null,
    });
  };

  const handleEditGrupo = (grupo: CgnatGrupo) => {
    setGeradorDialog({
      open: true,
      grupo,
    });
  };

  const handleCloseGerador = () => {
    setGeradorDialog({
      open: false,
      grupo: null,
    });
  };

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'primary')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <HubIcon sx={{ fontSize: 120, color: theme.palette.primary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}` }}>
              <HubIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gestão CGNAT</Typography>
              <Typography variant="caption" color="text.secondary">Grupos, blocos IP e scripts RouterOS</Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" size="small" onClick={() => setPesquisaDialogOpen(true)}>Pesquisar Cliente</Button>
            <Button variant="contained" size="small" startIcon={<CalculateIcon />} onClick={handleOpenGerador}>Gerar CGNAT</Button>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadGrupos}>Atualizar</Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {!loading && totalCount > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Total Grupos" valor={totalCount} icone={<HubIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Nesta Página" valor={grupos.length} icone={<InfoIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Página" valor={page + 1} subtitulo={`de ${Math.max(1, Math.ceil(totalCount / rowsPerPage))}`} icone={<CalculateIcon sx={{ color: theme.palette.secondary.main }} />} cor={theme.palette.secondary.main} bgCor={alpha(theme.palette.secondary.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
          <FilterListIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <TextField
          fullWidth
          size="small"
          label="Buscar por nome"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Digite o nome do grupo..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Grupos CGNAT</Typography>
          <Chip size="small" label={`${totalCount} grupo${totalCount !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>

        {loading ? (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando grupos...</Typography>
          </Box>
        ) : grupos.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <HubIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum grupo CGNAT encontrado</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={rankingTableHeadRowSx(theme)}>
                    <TableCell>Nome</TableCell>
                  <TableCell>Bloco IP Público</TableCell>
                  <TableCell>Range IP Privado</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grupos.map((grupo) => (
                  <TableRow key={grupo.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{grupo.nome}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip label={grupo.bloco_ip_publico} size="small" color="primary" />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip label={grupo.range_ip_privado} size="small" color="secondary" />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetalhes(grupo)}
                            color="info"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditGrupo(grupo)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ver Script RouterOS">
                          <IconButton
                            size="small"
                            onClick={() => handleViewScript(grupo)}
                            color="warning"
                          >
                            <CodeIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(grupo.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
            <Divider />
            <Box sx={tablePaginationWrapperSx(theme)}>
              <TablePagination
                component="div"
                size="small"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Linhas:"
                sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Modal Gerador CGNAT */}
      <GeradorCgnatDialog
        open={geradorDialog.open}
        grupo={geradorDialog.grupo}
        onClose={handleCloseGerador}
        onSuccess={loadGrupos}
      />

      {/* Modal de Detalhes */}
      <DetalhesDialog
        open={detalhesDialog.open}
        grupo={detalhesDialog.grupo}
        onClose={handleCloseDetalhes}
      />

      {/* Modal de Script RouterOS */}
      <ScriptDialog
        open={scriptDialog.open}
        grupo={scriptDialog.grupo}
        onClose={handleCloseScript}
      />

      {/* Modal Pesquisar Cliente */}
      <CgnatPesquisarClienteDialog
        open={pesquisaDialogOpen}
        onClose={() => setPesquisaDialogOpen(false)}
      />
    </Box>
  );
};

// Componente Gerador CGNAT
const GeradorCgnatDialog: React.FC<{
  open: boolean;
  grupo?: CgnatGrupo | null;
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ open, grupo, onClose, onSuccess }) => {
  const isEditing = Boolean(grupo?.id);
  const [nome, setNome] = useState('');
  const [ipPublico, setIpPublico] = useState('');
  const [ipPrivado, setIpPrivado] = useState('');
  const [chainInicial, setChainInicial] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    if (grupo) {
      setNome(grupo.nome);
      setIpPublico(grupo.bloco_ip_publico);
      setIpPrivado(grupo.range_ip_privado);
      setChainInicial(String(grupo.chain_inicial ?? 0));
    } else {
      setNome('');
      setIpPublico('');
      setIpPrivado('');
      setChainInicial('0');
    }
    setError(null);
  }, [open, grupo]);

  // Função para calcular quantidade de IPs públicos
  const calcularQtdIPsPublicos = () => {
    if (!ipPublico) return 0;
    const match = ipPublico.match(/\/(\d+)$/);
    if (!match) return 0;
    const cidr = parseInt(match[1]);
    return Math.pow(2, 32 - cidr);
  };

  // Função para calcular quantidade de IPs privados (a partir do CIDR)
  const calcularQtdIPsPrivados = () => {
    if (!ipPrivado) return 0;
    const match = ipPrivado.match(/\/(\d+)$/);
    if (!match) return 0;
    const cidr = parseInt(match[1]);
    return Math.pow(2, 32 - cidr);
  };

  // Função para calcular clientes por IP (calculado automaticamente)
  const calcularClientesPorIp = () => {
    const qtdPublicos = calcularQtdIPsPublicos();
    const qtdPrivados = calcularQtdIPsPrivados();
    if (qtdPublicos === 0) return 0;
    return Math.floor(qtdPrivados / qtdPublicos);
  };

  // Função para calcular portas por cliente
  const calcularPortasPorCliente = () => {
    const clientesPorIp = calcularClientesPorIp();
    if (clientesPorIp === 0) return 0;
    const portasDisponiveis = 65535 - 1024 + 1; // 64512 portas disponíveis
    return Math.floor(portasDisponiveis / clientesPorIp);
  };

  // Função para calcular range de IP privado (baseado no CIDR)
  const calcularRangeIP = () => {
    if (!ipPrivado) return 'N/A';
    
    // Verifica se tem CIDR
    const match = ipPrivado.match(/\/(\d+)$/);
    if (!match) return 'N/A';
    const cidr = parseInt(match[1]);
    
    // Pega o IP base privado (sem CIDR)
    const ipBasePrivado = ipPrivado.split('/')[0].trim();
    if (!ipBasePrivado) return 'N/A';
    
    // Converte o IP base para número
    const partesIP = ipBasePrivado.split('.').map(Number);
    if (partesIP.length !== 4 || partesIP.some(isNaN)) return 'N/A';
    
    // Calcula quantos IPs estão no range do CIDR
    const totalIPsPrivados = calcularQtdIPsPrivados();
    
    // Calcula o IP inicial (subnet base do CIDR)
    const mascaraBits = 32 - cidr;
    let ipInicialNum = (partesIP[0] << 24) + (partesIP[1] << 16) + (partesIP[2] << 8) + partesIP[3];
    // Zera os bits de host para obter o IP base da subnet
    ipInicialNum = (ipInicialNum >> mascaraBits) << mascaraBits;
    
    // Calcula o IP final
    let ipFinalNum = ipInicialNum + totalIPsPrivados - 1;
    
    // Converte de volta para string
    const ipInicial = [
      (ipInicialNum >>> 24) & 0xFF,
      (ipInicialNum >>> 16) & 0xFF,
      (ipInicialNum >>> 8) & 0xFF,
      ipInicialNum & 0xFF
    ].join('.');
    
    const ipFinal = [
      (ipFinalNum >>> 24) & 0xFF,
      (ipFinalNum >>> 16) & 0xFF,
      (ipFinalNum >>> 8) & 0xFF,
      ipFinalNum & 0xFF
    ].join('.');
    
    return `${ipInicial} - ${ipFinal}`;
  };

  const qtdIPsPublicos = calcularQtdIPsPublicos();
  const qtdIPsPrivados = calcularQtdIPsPrivados();
  const clientesPorIp = calcularClientesPorIp();
  const portasPorCliente = calcularPortasPorCliente();
  const rangeIP = calcularRangeIP();

  // Função para gerar todos os dados CGNAT (IPs e portas)
  const gerarDadosCgnat = () => {
    if (!ipPublico || !ipPrivado) return [];

    // Extrair IPs base e CIDRs
    const ipBasePublico = ipPublico.split('/')[0].trim();
    const ipBasePrivado = ipPrivado.split('/')[0].trim();

    if (!ipBasePublico || !ipBasePrivado) return [];

    // Converter IPs para número
    const partesIPPublico = ipBasePublico.split('.').map(Number);
    const partesIPPrivado = ipBasePrivado.split('.').map(Number);

    if (partesIPPublico.length !== 4 || partesIPPublico.some(isNaN)) return [];
    if (partesIPPrivado.length !== 4 || partesIPPrivado.some(isNaN)) return [];

    // Calcular CIDRs
    const matchPublico = ipPublico.match(/\/(\d+)$/);
    const matchPrivado = ipPrivado.match(/\/(\d+)$/);
    if (!matchPublico || !matchPrivado) return [];

    const cidrPublico = parseInt(matchPublico[1]);
    const cidrPrivado = parseInt(matchPrivado[1]);

    // Calcular quantidade de IPs
    const totalIPsPublicos = calcularQtdIPsPublicos();
    const totalIPsPrivados = calcularQtdIPsPrivados();
    const clientesPorIp = calcularClientesPorIp();
    const portasPorCliente = calcularPortasPorCliente();

    if (totalIPsPublicos === 0 || totalIPsPrivados === 0 || clientesPorIp === 0) return [];

    // Converter IP base público para número
    let ipBasePublicoNum = (partesIPPublico[0] << 24) + (partesIPPublico[1] << 16) + (partesIPPublico[2] << 8) + partesIPPublico[3];
    const mascaraBitsPublico = 32 - cidrPublico;
    ipBasePublicoNum = (ipBasePublicoNum >> mascaraBitsPublico) << mascaraBitsPublico;

    // Converter IP base privado para número
    let ipBasePrivadoNum = (partesIPPrivado[0] << 24) + (partesIPPrivado[1] << 16) + (partesIPPrivado[2] << 8) + partesIPPrivado[3];
    const mascaraBitsPrivado = 32 - cidrPrivado;
    ipBasePrivadoNum = (ipBasePrivadoNum >> mascaraBitsPrivado) << mascaraBitsPrivado;

    // Array para armazenar os dados
    const dados: any[] = [];
    let contadorIPPrivado = 0;
    let portaInicial = 1024;

    // A lógica correta: agrupar por ranges de portas
    // Cada range de portas é compartilhado por todos os IPs públicos
    while (contadorIPPrivado < totalIPsPrivados) {
      // Calcular porta final do range atual
      const portaFinal = Math.min(portaInicial + portasPorCliente - 1, 65535);

      // Para este range de portas, criar entrada para cada IP público
      for (let i = 0; i < totalIPsPublicos; i++) {
        // Verificar se não ultrapassou o limite de IPs privados
        if (contadorIPPrivado >= totalIPsPrivados) break;

        // Calcular IP público atual
        const ipPublicoNum = ipBasePublicoNum + i;
        const ipPublicoStr = [
          (ipPublicoNum >>> 24) & 0xFF,
          (ipPublicoNum >>> 16) & 0xFF,
          (ipPublicoNum >>> 8) & 0xFF,
          ipPublicoNum & 0xFF
        ].join('.');

        // Calcular IP privado atual
        const ipPrivadoNum = ipBasePrivadoNum + contadorIPPrivado;
        const ipPrivadoStr = [
          (ipPrivadoNum >>> 24) & 0xFF,
          (ipPrivadoNum >>> 16) & 0xFF,
          (ipPrivadoNum >>> 8) & 0xFF,
          ipPrivadoNum & 0xFF
        ].join('.');

        dados.push({
          ip_privado: ipPrivadoStr,
          ip_publico: ipPublicoStr,
          porta_inicial: portaInicial,
          porta_final: portaFinal
        });

        // Avançar para o próximo IP privado
        contadorIPPrivado++;
      }

      // Avançar para o próximo range de portas
      portaInicial = portaFinal + 1;

      // Se ultrapassou o limite de portas, reiniciar
      if (portaInicial > 65535) {
        portaInicial = 1024;
      }
    }

    return dados;
  };

  const handleReset = () => {
    if (isEditing && grupo) {
      setNome(grupo.nome);
      setIpPublico(grupo.bloco_ip_publico);
      setIpPrivado(grupo.range_ip_privado);
      setChainInicial(String(grupo.chain_inicial ?? 0));
    } else {
      setNome('');
      setIpPublico('');
      setIpPrivado('');
      setChainInicial('0');
    }
    setError(null);
  };

  const handleSubmit = async () => {
    // Validações
    if (!nome.trim()) {
      setError('O nome é obrigatório');
      return;
    }

    if (!ipPublico.trim()) {
      setError('O IP público é obrigatório');
      return;
    }

    if (!ipPublico.includes('/')) {
      setError('O IP público deve incluir o CIDR (ex: 200.2.3.0/27)');
      return;
    }

    if (!ipPrivado.trim()) {
      setError('O IP privado é obrigatório');
      return;
    }

    if (!ipPrivado.includes('/')) {
      setError('O IP privado deve incluir o CIDR (ex: 100.64.0.0/10)');
      return;
    }

    const chainInicialNum = parseInt(chainInicial, 10);
    if (Number.isNaN(chainInicialNum) || chainInicialNum < 0) {
      setError('Chain inicial deve ser um número inteiro maior ou igual a zero');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Gerar todos os dados CGNAT (IPs e portas)
      const dadosCgnat = gerarDadosCgnat();

      if (dadosCgnat.length === 0) {
        setError('Não foi possível gerar os dados CGNAT. Verifique os IPs informados.');
        setLoading(false);
        return;
      }

      const data = {
        nome: nome.trim(),
        bloco_ip_publico: ipPublico.trim(),
        range_ip_privado: ipPrivado.trim(),
        chain_inicial: chainInicialNum,
        dados: dadosCgnat
      };

      if (isEditing && grupo) {
        await api.put(`/cgnat/${grupo.id}`, data);
      } else {
        await api.post('/cgnat', data);
      }

      // Limpar formulário e fechar
      handleReset();
      onClose();
      
      // Chamar callback de sucesso (para recarregar a lista)
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Erro ao salvar grupo CGNAT:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao salvar grupo CGNAT');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing ? <EditIcon /> : <CalculateIcon />}
          {isEditing ? 'Editar CGNAT' : 'Gerador de CGNAT'}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Campo Nome */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: CGNAT Região Sul"
              variant="outlined"
              required
              error={error?.includes('nome') || false}
            />
          </Grid>

          {/* Campo IP Público */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="IP Público (com CIDR)"
              value={ipPublico}
              onChange={(e) => setIpPublico(e.target.value)}
              placeholder="Ex: 200.2.3.0/27"
              variant="outlined"
              helperText="Formato: x.x.x.x/CIDR"
              required
              error={error?.includes('público') || false}
            />
          </Grid>

          {/* Campo IP Privado */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="IP Privado (com CIDR)"
              value={ipPrivado}
              onChange={(e) => setIpPrivado(e.target.value)}
              placeholder="Ex: 100.64.0.0/10"
              variant="outlined"
              helperText="Formato: x.x.x.x/CIDR"
              required
              error={error?.includes('privado') || false}
            />
          </Grid>

          {/* Campo Chain Inicial */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Chain inicial"
              type="number"
              value={chainInicial}
              onChange={(e) => setChainInicial(e.target.value)}
              placeholder="Ex: 64"
              variant="outlined"
              helperText="Número inicial das chains RouterOS (ex: CGNAT_64, CGNAT_65...)"
              required
              inputProps={{ min: 0, step: 1 }}
              error={error?.includes('Chain inicial') || false}
            />
          </Grid>

          {/* Divisor */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Resultados Calculados
            </Typography>
          </Grid>

          {/* Visualizadores de Resultados */}
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Qtde IPs Públicos
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {qtdIPsPublicos.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Clientes por IP
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  {clientesPorIp}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Qtde IPs Privados
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {qtdIPsPrivados.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Portas por Cliente
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {portasPorCliente.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Range de IP */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Range de IP Privado
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', wordBreak: 'break-all', color: 'success.main' }}>
                  {rangeIP}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} variant="outlined" disabled={loading}>
          Limpar
        </Button>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente de Script RouterOS
const ScriptDialog: React.FC<{
  open: boolean;
  grupo: CgnatGrupo | null;
  onClose: () => void;
}> = ({ open, grupo, onClose }) => {
  const [script, setScript] = useState('');
  const [simplificado, setSimplificado] = useState(false);

  useEffect(() => {
    if (open && grupo) {
      setSimplificado(false);
      gerarScript(grupo);
    }
  }, [open, grupo]);

  useEffect(() => {
    if (open && grupo) {
      if (simplificado) {
        gerarScriptSimplificado(grupo);
      } else {
        gerarScript(grupo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simplificado]);

  // Converte IP para número para cálculos
  const ipParaNumero = (ip: string): number => {
    const partes = ip.split('.').map(Number);
    return partes[0] * 256 * 256 * 256 + partes[1] * 256 * 256 + partes[2] * 256 + partes[3];
  };

  // Converte número para IP
  const numeroParaIp = (num: number): string => {
    const parte3 = num % 256;
    num = Math.floor(num / 256);
    const parte2 = num % 256;
    num = Math.floor(num / 256);
    const parte1 = num % 256;
    num = Math.floor(num / 256);
    const parte0 = num % 256;
    return `${parte0}.${parte1}.${parte2}.${parte3}`;
  };

  // Gera todas as subnets de um determinado tamanho dentro de um range CIDR
  const gerarSubnets = (ipBase: string, cidrBase: number, cidrDestino: number): string[] => {
    const ipNum = ipParaNumero(ipBase);
    const hostsBase = Math.pow(2, 32 - cidrBase);
    const hostsDestino = Math.pow(2, 32 - cidrDestino);
    const qtdSubnets = hostsBase / hostsDestino;

    const subnets: string[] = [];
    for (let i = 0; i < qtdSubnets; i++) {
      const ipSubnet = numeroParaIp(ipNum + i * hostsDestino);
      subnets.push(ipSubnet);
    }

    return subnets;
  };

  const gerarScript = (grupoInfo: CgnatGrupo) => {
    if (!grupoInfo) {
      setScript('#CGNAT\n# Grupo não disponível');
      return;
    }

    let scriptTexto = '#CGNAT\n';

    const ipPublicoGrupo = grupoInfo.bloco_ip_publico;
    const rangeIpPrivado = grupoInfo.range_ip_privado;
    const chainInicial = grupoInfo.chain_inicial ?? 0;

    // Parsear CIDR do IP privado
    const matchPrivado = rangeIpPrivado.match(/^(.+?)\/(\d+)$/);
    if (!matchPrivado) {
      setScript('#CGNAT\n# Erro: range_ip_privado inválido');
      return;
    }

    const ipBasePrivado = matchPrivado[1];
    const cidrPrivado = parseInt(matchPrivado[2]);

    // 1. Gerar todas as subnets /24 do range privado
    const subnets24 = gerarSubnets(ipBasePrivado, cidrPrivado, 24);
    
    // 2. Gerar regras srcnat para cada /24
    subnets24.forEach(subnet24 => {
      const partes = subnet24.split('.');
      const nomeChain = `CGNAT_${partes[0]}_${partes[1]}_${partes[2]}`;
      scriptTexto += `/ip firewall nat add chain=srcnat src-address=${subnet24}/24   action=jump jump-target=${nomeChain}\n`;
    });

    scriptTexto += '\n';

    // 3. Para cada /24, gerar todas as subnets /28 e criar regras intermediárias
    let contadorIndice = 0;
    const mapSubnet28PorIndice = new Map<number, string>();

    subnets24.forEach(subnet24 => {
      const partes = subnet24.split('.');
      const nomeChain24 = `CGNAT_${partes[0]}_${partes[1]}_${partes[2]}`;
      
      // Gerar todas as subnets /28 dentro deste /24
      const subnets28 = gerarSubnets(subnet24, 24, 28);

      // Ordenar por octeto (já deve vir ordenado, mas garantindo)
      subnets28.sort((a, b) => {
        const octetoA = parseInt(a.split('.')[3]);
        const octetoB = parseInt(b.split('.')[3]);
        return octetoA - octetoB;
      });

      // Gerar regras intermediárias para cada /28
      subnets28.forEach(subnet28 => {
        mapSubnet28PorIndice.set(contadorIndice, subnet28);
        const chainNum = chainInicial + contadorIndice;
        scriptTexto += `/ip firewall nat add chain=${nomeChain24} src-address=${subnet28}/28 action=jump jump-target=CGNAT_${chainNum}\n`;
        contadorIndice++;
      });
    });

    scriptTexto += '\n';

    // 4. Gerar regras netmap com distribuição sequencial de portas
    // Calcular portas disponíveis e distribuir igualmente entre todas as subnets /28
    const portaInicialBase = 1024;
    const portaFinalMaxima = 65535;
    const portasDisponiveis = portaFinalMaxima - portaInicialBase + 1; // 64512 portas
    const totalSubnets28 = contadorIndice;
    const portasPorSubnet28 = Math.floor(portasDisponiveis / totalSubnets28); // Distribuir igualmente

    for (let indice = 0; indice < contadorIndice; indice++) {
      const subnet28 = mapSubnet28PorIndice.get(indice);
      if (!subnet28) continue;

      const chainNum = chainInicial + indice;
      const portaInicial = portaInicialBase + indice * portasPorSubnet28;
      const portaFinal = portaInicial + portasPorSubnet28 - 1;

      scriptTexto += `/ip firewall nat add action=netmap chain=CGNAT_${chainNum} protocol=tcp src-address=${subnet28}/28 to-addresses=${ipPublicoGrupo} to-ports=${portaInicial}-${portaFinal}\n`;
      scriptTexto += `/ip firewall nat add action=netmap chain=CGNAT_${chainNum} protocol=udp src-address=${subnet28}/28 to-addresses=${ipPublicoGrupo} to-ports=${portaInicial}-${portaFinal}\n`;
      scriptTexto += `/ip firewall nat add action=netmap chain=CGNAT_${chainNum} src-address=${subnet28}/28 to-addresses=${ipPublicoGrupo}\n`;
    }

    setScript(scriptTexto);
  };

  // Gera o script simplificado: sem a chain intermediária por /28,
  // as regras de netmap usam diretamente a chain do /24 (mesmo destino do jump da primeira parte)
  const gerarScriptSimplificado = (grupoInfo: CgnatGrupo) => {
    if (!grupoInfo) {
      setScript('#CGNAT\n# Grupo não disponível');
      return;
    }

    let scriptTexto = '#CGNAT\n';

    const ipPublicoGrupo = grupoInfo.bloco_ip_publico;
    const rangeIpPrivado = grupoInfo.range_ip_privado;

    // Parsear CIDR do IP privado
    const matchPrivado = rangeIpPrivado.match(/^(.+?)\/(\d+)$/);
    if (!matchPrivado) {
      setScript('#CGNAT\n# Erro: range_ip_privado inválido');
      return;
    }

    const ipBasePrivado = matchPrivado[1];
    const cidrPrivado = parseInt(matchPrivado[2]);

    // 1. Gerar todas as subnets /24 do range privado
    const subnets24 = gerarSubnets(ipBasePrivado, cidrPrivado, 24);

    // 2. Gerar regras srcnat para cada /24 (jump-target já aponta para a chain do /24)
    subnets24.forEach(subnet24 => {
      const partes = subnet24.split('.');
      const nomeChain = `CGNAT_${partes[0]}_${partes[1]}_${partes[2]}`;
      scriptTexto += `/ip firewall nat add chain=srcnat src-address=${subnet24}/24   action=jump jump-target=${nomeChain}\n`;
    });

    scriptTexto += '\n';

    // 3. Para cada /24, gerar direto as regras netmap para cada /28 usando a chain do /24
    // Calcular portas disponíveis e distribuir igualmente entre todas as subnets /28 (mesma lógica de antes)
    const todasSubnets28: { subnet28: string; nomeChain24: string }[] = [];

    subnets24.forEach(subnet24 => {
      const partes = subnet24.split('.');
      const nomeChain24 = `CGNAT_${partes[0]}_${partes[1]}_${partes[2]}`;

      const subnets28 = gerarSubnets(subnet24, 24, 28);
      subnets28.sort((a, b) => {
        const octetoA = parseInt(a.split('.')[3]);
        const octetoB = parseInt(b.split('.')[3]);
        return octetoA - octetoB;
      });

      subnets28.forEach(subnet28 => {
        todasSubnets28.push({ subnet28, nomeChain24 });
      });
    });

    const portaInicialBase = 1024;
    const portaFinalMaxima = 65535;
    const portasDisponiveis = portaFinalMaxima - portaInicialBase + 1; // 64512 portas
    const totalSubnets28 = todasSubnets28.length;
    const portasPorSubnet28 = Math.floor(portasDisponiveis / totalSubnets28); // Distribuir igualmente

    todasSubnets28.forEach(({ subnet28, nomeChain24 }, indice) => {
      const portaInicial = portaInicialBase + indice * portasPorSubnet28;
      const portaFinal = portaInicial + portasPorSubnet28 - 1;

      scriptTexto += `/ip firewall nat add action=netmap chain=${nomeChain24} protocol=tcp src-address=${subnet28}/28 to-addresses=${ipPublicoGrupo} to-ports=${portaInicial}-${portaFinal}\n`;
      scriptTexto += `/ip firewall nat add action=netmap chain=${nomeChain24} protocol=udp src-address=${subnet28}/28 to-addresses=${ipPublicoGrupo} to-ports=${portaInicial}-${portaFinal}\n`;
      scriptTexto += `/ip firewall nat add action=netmap chain=${nomeChain24} src-address=${subnet28}/28 to-addresses=${ipPublicoGrupo}\n`;
    });

    setScript(scriptTexto);
  };

  if (!grupo) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon />
          Script RouterOS - {grupo.nome}
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          rows={25}
          value={script}
          variant="outlined"
          InputProps={{
            readOnly: true,
            sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant={simplificado ? 'contained' : 'outlined'}
          color="secondary"
          onClick={() => setSimplificado((prev) => !prev)}
        >
          {simplificado ? 'Ver Script Completo' : 'Simplificar'}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (script) {
              navigator.clipboard.writeText(script);
            }
          }}
          disabled={!script}
        >
          Copiar Script
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente de Detalhes
const DetalhesDialog: React.FC<{
  open: boolean;
  grupo: CgnatGrupo | null;
  onClose: () => void;
}> = ({ open, grupo, onClose }) => {
  const [dados, setDados] = useState<CgnatDado[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && grupo) {
      loadDados();
    }
  }, [open, grupo]);

  const loadDados = async () => {
    if (!grupo) return;

    try {
      const response: any = await api.get(`/cgnat/${grupo.id}/dados`);

      if (response && response.success) {
        setDados(response.data);
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!grupo) return null;

  const rangePublicoInfo = calcularInfoRangeIp(grupo.bloco_ip_publico);
  const rangePrivadoInfo = calcularInfoRangeIp(grupo.range_ip_privado);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon />
          Detalhes - {grupo.nome}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Informações do Grupo
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
                  {grupo.nome}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Bloco IP Público
                    </Typography>
                    <Chip label={grupo.bloco_ip_publico} size="small" color="primary" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      IP Inicial Público
                    </Typography>
                    <Chip
                      label={rangePublicoInfo?.ipInicial ?? '—'}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      IP Final Público
                    </Typography>
                    <Chip
                      label={rangePublicoInfo?.ipFinal ?? '—'}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Range IP Privado
                    </Typography>
                    <Chip label={grupo.range_ip_privado} size="small" color="secondary" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      IP Inicial Privado
                    </Typography>
                    <Chip
                      label={rangePrivadoInfo?.ipInicial ?? '—'}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      IP Final Privado
                    </Typography>
                    <Chip
                      label={rangePrivadoInfo?.ipFinal ?? '—'}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      Qtde IPs Privados
                    </Typography>
                    <Chip
                      label={rangePrivadoInfo ? rangePrivadoInfo.qtdIps.toLocaleString('pt-BR') : '—'}
                      size="small"
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      Chain Inicial
                    </Typography>
                    <Chip label={String(grupo.chain_inicial ?? 0)} size="small" color="warning" />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Dados CGNAT
            </Typography>
            {loading ? (
              <CircularProgress />
            ) : dados.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Nenhum dado CGNAT cadastrado ainda
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>IP Privado</TableCell>
                      <TableCell>IP Público</TableCell>
                      <TableCell align="center">Porta Inicial</TableCell>
                      <TableCell align="center">Porta Final</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dados.map((dado) => (
                      <TableRow key={dado.id}>
                        <TableCell>{dado.ip_privado}</TableCell>
                        <TableCell>{dado.ip_publico}</TableCell>
                        <TableCell align="center">{dado.porta_inicial}</TableCell>
                        <TableCell align="center">{dado.porta_final}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CgnatPage;
